import { addMonths, addYears, msToParts, partsToMs } from './calendar.js';
import { createCtx } from './ctx.js';
import { isScene } from './scene.js';
import { TIME_FIELDS, formatStamp, isDocked, legacyTimes, posAtTime, timeAt, timeValueOf } from './timeline.js';
import { isDuration, markMoment, momentOf, momentText } from './timeMarks.js';
import { TIME_PRECISIONS, TIME_SOURCES, type NodeType, type SceneTime, type Story, type StoryNode, type TimeDisplay, type TimelineData, type TimePrecision } from './types.js';

/*
 * Zeit einer Szene (PLAN.md §15, „Zeit zuerst“): Die Zeit ist eine Angabe der Szene (`StoryNode.time`), nicht ihre Lage auf der Fläche.
 * Solange Szenen noch an Zeitstrahlen der Zeichenfläche angedockt werden, gilt: Hat der Zeitstrahl eine Zeitrechnung, folgt die Lage aus
 * der Zeit (`placementOf`); wird die Szene gezogen, wird die Zeit an der neuen Stelle abgelesen (`timeAtPlacement`). Ohne Zeitrechnung
 * gibt es nur die Lage.
 *
 * Die Form folgt der Zeit (PLAN.md §17): nur ein Beginn – ein Zeitpunkt („Ereignis“); Beginn und Ende – ein Zeitraum. Das Ende lässt
 * sich als Zeit oder als Dauer angeben (`duration`, dann folgt das Ende aus Beginn + Dauer). Ein Ende entsteht nie von selbst.
 */

type SceneKind = 'event' | 'period';

/** Form, die eine Zeit verlangt: mit Ende ein Zeitraum, nur mit Beginn ein Zeitpunkt; ohne Beginn keine (die Form bleibt, wie sie ist). */
export function timeKind(time: SceneTime | null | undefined): SceneKind | null {
  if (!time?.start) return null;
  return time.end ? 'period' : 'event';
}

/** Die früheren Zeitfelder (in Szenen und in der Klasse Szene), die `time` ersetzt */
const LEGACY_KEYS = new Set<string>(Object.values(TIME_FIELDS));
const isLegacyTimeField = (f: { key: string; type: string }): boolean => LEGACY_KEYS.has(f.key) && f.type === 'date';

/**
 * Zeitangabe in Normalform: leere Werte → null; mit einer Dauer ist das Ende Beginn + Dauer; Vorgaben (Minute, von Hand) weggelassen;
 * ganz leer (ohne Zeit und ohne Anker) → null.
 */
export function normalizeSceneTime(time: SceneTime | null | undefined): SceneTime | null {
  if (!time) return null;
  const start = time.start?.trim() || null;
  let end = time.end?.trim() || null;
  const duration = time.duration?.trim() || null;
  if (duration && start && isDuration(duration)) {
    const m = markMoment(`+${duration}`, momentOf(start));
    if (m) end = momentText(m);
  }
  if (start === null && end === null && !time.anchor?.scene) return null;
  const out: SceneTime = { start, end };
  if (duration) out.duration = duration;
  if (time.precision && time.precision !== 'minute') out.precision = time.precision;
  if (time.approx) out.approx = true;
  if (time.source && time.source !== 'manual') out.source = time.source;
  if (time.anchor?.scene) out.anchor = time.anchor.offset ? { scene: time.anchor.scene, offset: time.anchor.offset } : { scene: time.anchor.scene };
  return out;
}

/** Prüft eine Zeitangabe einer Szene. Liefert eine Fehlermeldung oder null. */
export function checkSceneTime(time: SceneTime | null): string | null {
  if (!time) return null;
  const notATime = (v: string): string => `„${v}“ ist keine Zeit (Datum JJJJ-MM-TTThh:mm oder eigene Zählung wie „Tag 12, 06:00“).`;
  if (time.start !== null && !timeValueOf(time.start)) return notATime(time.start);
  if (time.duration !== undefined && !isDuration(time.duration)) return `„${time.duration}“ ist keine Dauer (z. B. 3d, 2h, 45min, 1w, 2mo, 1y).`;
  if (time.end !== null) {
    const end = timeValueOf(time.end);
    if (!end) return notATime(time.end);
    const start = time.start !== null ? timeValueOf(time.start) : null;
    if (start && start.basis === end.basis && end.value <= start.value) return 'Das Ende muss nach dem Beginn liegen.';
  }
  if (time.precision !== undefined && !(TIME_PRECISIONS as readonly string[]).includes(time.precision)) return 'Unbekannte Genauigkeit.';
  if (time.source !== undefined && !(TIME_SOURCES as readonly string[]).includes(time.source)) return 'Unbekannte Herkunft der Zeit.';
  if (time.anchor?.offset !== undefined && !/^[+-]\d{1,6}(min|h|d|w|mo|y)(@\d{2}:\d{2})?$/.test(time.anchor.offset)) return `„${time.anchor.offset}“ ist kein Abstand (z. B. +3d, -2w, +1d@07:00).`;
  return null;
}

/** Lage einer angedockten Szene auf dem Zeitstrahl */
export interface Placement {
  at: number | null;
  from: number | null;
  to: number | null;
}

/**
 * Lage einer angedockten Szene zu ihrer Zeit, in der Form, die die Zeit verlangt: ein Zeitpunkt an seiner Stelle, ein Zeitraum von
 * Beginn bis Ende. null, wenn der Zeitstrahl die Zeit nicht abbildet (keine Zeit, keine Zeitrechnung, eine andere Vergleichsbasis).
 */
export function placementOf(t: TimelineData, time: SceneTime | null | undefined): (Placement & { kind: SceneKind }) | null {
  if (!time?.start) return null;
  const a = posAtTime(t, time.start);
  if (a === null || !Number.isFinite(a)) return null;
  const b = time.end ? posAtTime(t, time.end) : null;
  if (b !== null && Number.isFinite(b) && b > a) return { kind: 'period', at: null, from: a, to: b };
  return { kind: 'event', at: a, from: null, to: null };
}

/**
 * Zeit an einer Lage auf dem Zeitstrahl (Herkunft „Zeitlinie“); Genauigkeit und „ungefähr“ der bisherigen Angabe bleiben, eine Dauer
 * nicht (gezogen gilt das Ende an der Stelle). null ohne Zeitrechnung.
 */
export function timeAtPlacement(t: TimelineData, kind: SceneKind, place: Placement, prev?: SceneTime | null): SceneTime | null {
  const pos = kind === 'event' ? place.at : place.from;
  if (pos === null) return null;
  const start = timeAt(t, pos);
  if (start === null) return null;
  const end = kind === 'period' && place.to !== null ? timeAt(t, place.to) : null;
  return normalizeSceneTime({ start, end, precision: prev?.precision, approx: prev?.approx, source: 'timeline' });
}

/** Welche Teile einer Zeit eine Genauigkeit zeigt (fehlend: alle). */
export function precisionDisplay(p: TimePrecision | undefined): TimeDisplay {
  if (p === 'year') return { year: true, month: false, day: false, time: false };
  if (p === 'month') return { year: true, month: true, day: false, time: false };
  if (p === 'day') return { year: true, month: true, day: true, time: false };
  return { year: true, month: true, day: true, time: true };
}

/**
 * Zeitwert einer Szene als Text, begrenzt auf ihre Genauigkeit und die Zeitanzeige (`display`): „03.03.1823 07:00“, „03.1823“,
 * „ca. 1823“. Leerer Wert → leerer Text.
 */
export function formatSceneTime(value: string | null, time: Pick<SceneTime, 'precision' | 'approx'> | null | undefined, display?: TimeDisplay): string {
  if (!value) return '';
  const p = precisionDisplay(time?.precision);
  const shown: TimeDisplay = display ? { year: p.year && display.year, month: p.month && display.month, day: p.day && display.day, time: p.time && display.time } : p;
  const text = formatStamp(value, shown);
  return time?.approx ? `ca. ${text}` : text;
}

/** Spanne in der Vergleichsbasis (`timeValueOf`): Datum in Millisekunden, eigene Zählung in Minuten */
export interface FuzzySpan {
  basis: 'ms' | 'count';
  start: number;
  end: number;
}

type FuzzyUnit = Exclude<TimePrecision, 'minute'>;
/** Länge einer Einheit in Minuten bei eigener Zählung (wie `sameAt`) */
const COUNT_STEP: Record<FuzzyUnit, number> = { year: 525960, month: 43200, day: 1440, hour: 60 };

/** Beginn der Einheit, in der `ms` liegt (im Kalender der Geschichte) */
function floorMs(ms: number, u: FuzzyUnit): number | null {
  const p = msToParts(ms);
  if (u === 'year') return partsToMs({ y: p.y, mo: 1, d: 1, h: 0, mi: 0 });
  if (u === 'month') return partsToMs({ ...p, d: 1, h: 0, mi: 0 });
  if (u === 'day') return partsToMs({ ...p, h: 0, mi: 0 });
  return partsToMs({ ...p, mi: 0 });
}
/** `ms` plus `n` Einheiten (Monate und Jahre nach dem Kalender) */
function addMs(ms: number, u: FuzzyUnit, n: number): number {
  if (u === 'year') return addYears(ms, n);
  if (u === 'month') return addMonths(ms, n);
  return ms + n * (u === 'day' ? 86_400_000 : 3_600_000);
}

/**
 * Spanne, die eine ungenaue Zeit offenlässt (AP-19, PF 58): vom Beginn der Einheit ihrer Genauigkeit, in der der Beginn liegt, bis zum
 * Ende der Einheit, in der das Ende (ohne Ende: der Beginn) liegt – „im März 1823“ der ganze März, „1823“ das ganze Jahr, „am 3. März“
 * der Tag. Ungefähr kommt eine Einheit zu beiden Seiten dazu („ca. 1823“: 1822 bis 1824, „gegen acht“: 7 bis 10 Uhr; minutengenau und
 * ungefähr: eine Stunde). null bei einer genauen Zeit (minutengenau, nicht ungefähr) bzw. ohne Beginn.
 */
export function fuzzySpan(time: SceneTime | null | undefined): FuzzySpan | null {
  if (!time?.start) return null;
  const p = time.precision ?? 'minute';
  if (p === 'minute' && !time.approx) return null;
  const u: FuzzyUnit = p === 'minute' ? 'hour' : p;
  const a = timeValueOf(time.start);
  const b = time.end ? timeValueOf(time.end) : null;
  if (!a) return null;
  const last = b && b.basis === a.basis && b.value > a.value ? b.value : a.value;
  const pad = time.approx ? 1 : 0;
  if (a.basis === 'count') {
    const step = COUNT_STEP[u];
    return { basis: 'count', start: (Math.floor(a.value / step) - pad) * step, end: (Math.floor(last / step) + 1 + pad) * step };
  }
  const lo = floorMs(a.value, u);
  const hi = floorMs(last, u);
  if (lo === null || hi === null) return null;
  return { basis: 'ms', start: addMs(lo, u, -pad), end: addMs(hi, u, 1 + pad) };
}

/** Übernahme älterer Daten: geänderte Szenen und die Klasse Szene ohne die früheren Zeitfelder (null, wenn sie keine mehr hat) */
export interface SceneTimeMigration {
  nodes: StoryNode[];
  sceneType: NodeType | null;
}

/**
 * Ältere Szenen bekommen ihre Zeit: angedockt aus der Lage auf dem Zeitstrahl (Herkunft „Zeitlinie“), sonst aus den früheren
 * Zeitfeldern. Die Zeitfelder fallen aus den Szenen und aus der Klasse Szene. Betroffen ist, wer noch Zeitfelder hat oder angedockt an
 * einem Zeitstrahl mit Zeitrechnung noch keine eigene Zeit – so braucht es keine Unterscheidung zwischen „fehlt“ und „keine“ (eine neue
 * Spalte in der Datenbank ist für alle zunächst leer). Dazu folgt die Form der Zeit (PLAN.md §17): ein loser Zeitraum ohne Ende wird ein
 * Zeitpunkt. Rein und idempotent; null, wenn nichts zu tun ist.
 */
export function migrateSceneTimes(story: Story): SceneTimeMigration | null {
  const ctx = createCtx(story);
  const nodes: StoryNode[] = [];
  for (const n of story.nodes) {
    if (!isScene(n)) continue;
    const legacyFields = Object.keys(n.fields).some((k) => LEGACY_KEYS.has(k));
    const derived = legacyTimes(ctx, n);
    const dockedWithout = derived.docked && !n.time?.start && derived.start !== null;
    const wrongKind = !isDocked(ctx, n) && timeKind(n.time) !== null && timeKind(n.time) !== n.kind;
    if (!legacyFields && !dockedWithout && !wrongKind) continue;
    const time = n.time?.start || (!derived.docked && n.time) ? n.time : normalizeSceneTime({ start: derived.start, end: derived.end, source: derived.docked ? 'timeline' : 'manual' });
    const fields = Object.fromEntries(Object.entries(n.fields).filter(([k]) => !LEGACY_KEYS.has(k)));
    // lose bzw. nicht auf der Fläche: die Form folgt der Zeit (angedockt folgt sie der Lage)
    const kind = !derived.docked ? (timeKind(time) ?? n.kind) : n.kind;
    nodes.push({ ...n, kind, time: time ?? null, fields });
  }
  const type = story.types.find((t) => t.builtin && t.category === 'scene');
  const sceneType = type && type.fields.some(isLegacyTimeField) ? { ...type, fields: type.fields.filter((f) => !isLegacyTimeField(f)) } : null;
  return nodes.length > 0 || sceneType ? { nodes, sceneType } : null;
}

/**
 * Zeit aus Feldwerten, wie sie ältere Aufrufe und Ordner noch mitgeben (`time` bzw. `start`/`end`): die Zeitangabe und die Felder ohne
 * diese Werte. Ohne solche Werte bleibt `time` undefined.
 */
export function splitLegacyTime(fields: Record<string, unknown> | undefined): { time: SceneTime | null | undefined; fields: Record<string, unknown> | undefined } {
  if (!fields || !Object.keys(fields).some((k) => LEGACY_KEYS.has(k))) return { time: undefined, fields };
  const str = (v: unknown): string | null => (typeof v === 'string' && v.trim() !== '' ? v.trim() : null);
  const time = normalizeSceneTime({ start: str(fields[TIME_FIELDS.event]) ?? str(fields[TIME_FIELDS.periodStart]), end: str(fields[TIME_FIELDS.periodEnd]) });
  return { time, fields: Object.fromEntries(Object.entries(fields).filter(([k]) => !LEGACY_KEYS.has(k))) };
}
